import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Clock, Sun, Moon } from 'lucide-react';

const schedule = [
  {
    day: 'Monday',
    morning: { time: '6:00 AM - 7:30 AM', program: 'MMA' },
    evening: { time: '7:30 PM - 9:00 PM', program: 'Muay Thai' },
  },
  {
    day: 'Tuesday',
    morning: { time: '6:00 AM - 7:30 AM', program: 'BJJ' },
    evening: { time: '7:30 PM - 9:00 PM', program: 'Boxing' },
  },
  {
    day: 'Wednesday',
    morning: { time: '6:00 AM - 7:30 AM', program: 'MMA' },
    evening: { time: '7:30 PM - 9:00 PM', program: 'Sparring' },
  },
  {
    day: 'Thursday',
    morning: { time: '6:00 AM - 7:30 AM', program: 'Kickboxing' },
    evening: { time: '7:30 PM - 9:00 PM', program: 'Conditioning' },
  },
  {
    day: 'Friday',
    morning: { time: '6:00 AM - 7:30 AM', program: 'MMA' },
    evening: { time: '7:30 PM - 9:00 PM', program: 'Sparring' },
  },
  {
    day: 'Saturday',
    morning: { time: '6:00 AM - 7:30 AM', program: 'Open Training' },
    evening: { time: '7:30 PM - 9:00 PM', program: 'Competition Training' },
  },
  {
    day: 'Sunday',
    morning: { time: '-', program: 'Holiday' },
    evening: { time: '-', program: 'Holiday' },
    isHoliday: true,
  },
];

const ScheduleSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="schedule"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#050505]"
      data-testid="schedule-section"
    >
      {/* Background terminal effect */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="h-full w-full bg-[linear-gradient(rgba(220,38,38,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(220,38,38,0.1)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-[#DC2626] text-sm font-bold tracking-[0.3em] uppercase">
            Training Schedule
          </span>
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Weekly<br />
            <span className="text-[#DC2626]">Training Times</span>
          </h2>
          <p className="text-neutral-400 max-w-xl mx-auto">
            Two sessions daily. Morning warriors and evening fighters. Choose your time to train.
          </p>
        </motion.div>

        {/* Schedule Table - Terminal Style */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-[#0A0A0A] border border-[#262626] overflow-hidden"
        >
          {/* Terminal header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-[#121212] border-b border-[#262626]">
            <div className="w-3 h-3 rounded-full bg-[#DC2626]" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="ml-4 text-neutral-500 text-sm font-mono">training_schedule.sh</span>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="schedule-table w-full">
              <thead>
                <tr className="bg-[#0F0F0F]">
                  <th className="text-left py-4 px-6 text-[#DC2626] text-sm font-bold tracking-[0.2em] uppercase border-b border-[#262626]">
                    Day
                  </th>
                  <th className="text-left py-4 px-6 text-[#DC2626] text-sm font-bold tracking-[0.2em] uppercase border-b border-[#262626]">
                    <div className="flex items-center gap-2">
                      <Sun size={16} />
                      <span>Morning</span>
                    </div>
                  </th>
                  <th className="text-left py-4 px-6 text-[#DC2626] text-sm font-bold tracking-[0.2em] uppercase border-b border-[#262626]">
                    <div className="flex items-center gap-2">
                      <Moon size={16} />
                      <span>Evening</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((item, index) => (
                  <motion.tr
                    key={item.day}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.05 }}
                    className={`border-b border-[#1A1A1A] hover:bg-[#121212] transition-colors ${item.isHoliday ? 'opacity-60' : ''}`}
                  >
                    <td className="py-4 px-6">
                      <span className={`font-['Oswald'] text-lg uppercase tracking-wide ${item.isHoliday ? 'text-neutral-500' : 'text-white'}`}>
                        {item.day}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      {item.isHoliday ? (
                        <span className="text-neutral-500 italic">Closed</span>
                      ) : (
                        <div className="flex flex-col">
                          <span className="text-white font-medium">{item.morning.program}</span>
                          <span className="text-neutral-500 text-sm flex items-center gap-1">
                            <Clock size={12} />
                            {item.morning.time}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      {item.isHoliday ? (
                        <span className="text-neutral-500 italic">Closed</span>
                      ) : (
                        <div className="flex flex-col">
                          <span className="text-white font-medium">{item.evening.program}</span>
                          <span className="text-neutral-500 text-sm flex items-center gap-1">
                            <Clock size={12} />
                            {item.evening.time}
                          </span>
                        </div>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-8 p-4 bg-[#121212] border border-[#262626]"
        >
          <p className="text-center text-white font-medium">
            <span className="text-[#DC2626] font-bold">Kids Batch:</span> 6:30 PM - 7:30 PM (Monday to Saturday)
          </p>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="text-center text-neutral-500 text-sm mt-4"
        >
          * Schedule subject to change. Contact us for the latest timings.
        </motion.p>
      </div>
    </section>
  );
};

export default ScheduleSection;
